import { Check, Copy, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import {
  buildCharset,
  entropyBits,
  generate,
  type PasswordOptions,
  type Strength,
  strength,
} from "../lib/password";

/** Uniform random index in [0, max) via CSPRNG with rejection sampling. */
function secureIndex(max: number): number {
  const limit = Math.floor(0x1_0000_0000 / max) * max;
  const buf = new Uint32Array(1);
  let x = 0;
  do {
    crypto.getRandomValues(buf);
    x = buf[0];
  } while (x >= limit);
  return x % max;
}

const STRENGTH: Record<
  Strength,
  { label: () => string; bars: number; color: string }
> = {
  weak: { label: () => m.pw_strength_weak(), bars: 1, color: "bg-rose-500" },
  fair: { label: () => m.pw_strength_fair(), bars: 2, color: "bg-amber-500" },
  strong: {
    label: () => m.pw_strength_strong(),
    bars: 3,
    color: "bg-lime-500",
  },
  "very-strong": {
    label: () => m.pw_strength_verystrong(),
    bars: 4,
    color: "bg-emerald-500",
  },
};

const TOGGLES = [
  { key: "upper", label: () => m.pw_uppercase() },
  { key: "lower", label: () => m.pw_lowercase() },
  { key: "digits", label: () => m.pw_digits() },
  { key: "symbols", label: () => m.pw_symbols() },
  { key: "excludeAmbiguous", label: () => m.pw_exclude_ambiguous() },
] as const;

export function PasswordGenerator() {
  const uid = useId();
  const [opts, setOpts] = useState<PasswordOptions>({
    length: 16,
    upper: true,
    lower: true,
    digits: true,
    symbols: true,
    excludeAmbiguous: false,
  });
  const [password, setPassword] = useState("");
  const [copied, setCopied] = useState(false);

  const charset = buildCharset(opts);
  const bits = entropyBits(opts.length, charset.length);
  const meter = STRENGTH[strength(bits)];

  const regenerate = useCallback(() => {
    setCopied(false);
    setPassword(charset ? generate(opts.length, charset, secureIndex) : "");
  }, [charset, opts.length]);

  // Generate on mount and whenever options change. Client-only (the SSR markup
  // has an empty field), so there is no hydration mismatch.
  useEffect(() => {
    regenerate();
  }, [regenerate]);

  const copy = async () => {
    if (!password) return;
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — ignore
    }
  };

  const setOpt = <K extends keyof PasswordOptions>(
    key: K,
    value: PasswordOptions[K],
  ) => setOpts((o) => ({ ...o, [key]: value }));

  return (
    <div className="space-y-6 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
      {/* Output */}
      <div className="flex gap-2">
        <input
          readOnly
          data-testid="password-output"
          aria-label={m.pw_output_label()}
          value={password}
          placeholder={charset ? "" : m.pw_select_charset()}
          className="h-12 flex-1 rounded-lg border border-border bg-background px-4 font-mono text-lg tracking-wide"
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-12 w-12 shrink-0"
          aria-label={copied ? m.pw_copied() : m.pw_copy()}
          onClick={copy}
        >
          {copied ? (
            <Check className="h-5 w-5 text-emerald-500" />
          ) : (
            <Copy className="h-5 w-5" />
          )}
        </Button>
        <Button
          type="button"
          size="icon"
          className="h-12 w-12 shrink-0"
          aria-label={m.pw_generate()}
          onClick={regenerate}
        >
          <RefreshCw className="h-5 w-5" />
        </Button>
      </div>

      {/* Strength */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{m.pw_strength()}</span>
          <span className="font-medium" data-testid="password-strength">
            {meter.label()} · {m.pw_entropy({ bits: String(Math.round(bits)) })}
          </span>
        </div>
        <div className="flex gap-1.5" aria-hidden>
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full ${
                i < meter.bars ? meter.color : "bg-border"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Length */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor={`${uid}-length`}>{m.pw_length()}</Label>
          <span className="font-mono text-sm tabular-nums">{opts.length}</span>
        </div>
        <input
          id={`${uid}-length`}
          type="range"
          min={4}
          max={64}
          value={opts.length}
          onChange={(e) => setOpt("length", Number(e.target.value))}
          className="w-full accent-primary"
        />
      </div>

      {/* Toggles */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {TOGGLES.map((t) => (
          <label
            key={t.key}
            className="flex items-center gap-2.5 rounded-lg border border-border bg-card/40 px-3 py-2 text-sm"
          >
            <input
              type="checkbox"
              checked={opts[t.key]}
              onChange={(e) => setOpt(t.key, e.target.checked)}
              className="h-4 w-4 accent-primary"
            />
            {t.label()}
          </label>
        ))}
      </div>
    </div>
  );
}
